(() => {
  if (customElements.get('nuwa-reviews-controller')) return;
  customElements.define('nuwa-reviews-controller', class extends HTMLElement {
    connectedCallback() {
      this.states = new Map();
      this.frame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.observer = new MutationObserver(() => this.schedule());
        this.observer.observe(this, {subtree: true, childList: true, attributes: true, attributeFilter: ['data-number-of-reviews']});
        this.sync();
      });
    }
    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.sync());
    }
    sync() {
      for (const [widget, state] of this.states) {
        if (!this.contains(widget)) {
          state.details.remove();
          this.states.delete(widget);
        }
      }
      this.querySelectorAll('.jdgm-review-widget, #judgeme_product_reviews').forEach(widget => {
        const title = widget.querySelector('.jm-review-widget-minimal-header__title, .jm-review-widget__title, .jdgm-rev-widg__title');
        if (!title) return; // Wait for the app to render its real widget.
        const modern = widget.querySelector('.jm-review-widget');
        if (modern && !widget.querySelector('.jm-no-reviews-state')) { this.restore(widget); return; }
        const counter = widget.querySelector('[data-number-of-reviews]');
        const raw = counter?.getAttribute('data-number-of-reviews') ?? widget.getAttribute('data-number-of-reviews') ?? this.dataset.reviewCount;
        const count = Number(raw);
        if (raw == null || raw.trim() === '' || !Number.isFinite(count) || count < 0) return;
        // Existing reviews always retain the original, unmodified Judge.me layout.
        if (count > 0 || widget.querySelector('.jdgm-rev')) {
          this.restore(widget);
          return;
        }
        if (this.states.has(widget)) return;
        const details = document.createElement('details');
        details.className = 'nuwa-empty-reviews';
        details.style.maxWidth = widget.style.maxWidth || '1200px';
        details.style.margin = '0 auto';
        const summary = document.createElement('summary');
        summary.className = 'nuwa-empty-reviews__title';
        const titleStyle = getComputedStyle(title);
        for (const property of ['font-family', 'font-size', 'font-weight', 'line-height', 'letter-spacing', 'text-transform', 'color']) {
          summary.style.setProperty(property, titleStyle.getPropertyValue(property));
        }
        const heading = document.createElement('span');
        heading.textContent = title.textContent.trim() || 'Customer Reviews';
        const icon = document.createElement('span');
        icon.className = 'nuwa-empty-reviews__icon';
        icon.setAttribute('aria-hidden', 'true');
        summary.append(heading, icon);
        details.append(summary);
        widget.before(details);
        details.append(widget);
        const state = {details, stylesReady: false, pendingClick: false};
        this.states.set(widget, state);
        // Expanding only reveals the app's original button. Never open the form here.
        details.addEventListener('toggle', () => {
          if (details.open && modern) this.prepareFormStyles(state);
        });
        state.onWriteClick = async event => {
          const button = event.target.closest('[data-testid="write-review-button"]');
          if (!modern || !button || state.stylesReady) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (state.pendingClick) return;
          state.pendingClick = true;
          await this.prepareFormStyles(state);
          state.pendingClick = false;
          if (this.isConnected && details.open && this.states.has(widget)) button.click();
        };
        widget.addEventListener('click', state.onWriteClick, true);
      });
    }
    prepareFormStyles(state) {
      if (state.stylesPromise) return state.stylesPromise;
      // The current Judge.me widget leaves native form CSS in prefetch mode.
      state.stylesPromise = Promise.all(
        [...document.querySelectorAll('link[rel="stylesheet"][href*="widget_v3_"]')].map(link => new Promise(resolve => {
          if (link.sheet) { link.media = 'all'; resolve(); return; }
          link.addEventListener('load', resolve, {once: true});
          link.addEventListener('error', resolve, {once: true});
          link.media = 'all';
          setTimeout(resolve, 5000);
        }))
      ).then(() => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve))))
        .then(() => { state.stylesReady = true; });
      return state.stylesPromise;
    }
    restore(widget) {
      const state = this.states.get(widget);
      if (!state) return;
      widget.removeEventListener('click', state.onWriteClick, true);
      state.details.before(widget);
      state.details.remove();
      this.states.delete(widget);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      for (const widget of this.states.keys()) this.restore(widget);
    }
  });
})();
